import React, { useState, useEffect } from 'react';
import Icon from '../AppIcon'; 
import Button from './Button';
import Input from './Input';
import Modal from './Modal';
import { supabase } from '../../lib/supabase'; 
import { useAuth } from '../../contexts/AuthContext';

import { userService } from '../../services/userService';

const LoginSignupModal = ({ isOpen, onClose, onSuccess, initialMode = 'login', message }) => {
  const { signIn } = useAuth();
  const [isLoginView, setIsLoginView] = useState(initialMode === 'login');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState(''); 
  const [lastName, setLastName] = useState(''); 
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Agreement States
  const [showAgreement, setShowAgreement] = useState(false);
  const [agreementText, setAgreementText] = useState('');
  const [agreementVersionId, setAgreementVersionId] = useState(null);
  const [pendingUserId, setPendingUserId] = useState(null);

  // Update view when initialMode changes or modal opens
  React.useEffect(() => {
    if (isOpen) {
      setIsLoginView(initialMode === 'login');
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setFirstName('');
      setLastName('');
      setError('');
      setSuccessMessage('');
      setShowAgreement(false);
    }
  }, [isOpen, initialMode]);

  const handleAcceptAgreement = async () => {
      try {
          setLoading(true);
          if (pendingUserId && agreementVersionId) {
             const { error: agErr } = await supabase.from('seller_agreements').insert({
                 user_id: pendingUserId,
                 version_id: agreementVersionId,
                 user_agent: navigator.userAgent
             });
             if (agErr && agErr.code !== '23505') console.error('Error recording agreement:', agErr);
          }
          
          setSuccessMessage('Logged in successfully!');
          setShowAgreement(false);
          onSuccess(); 
          onClose();
      } catch (err) {
          console.error(err);
          setError('Failed to record agreement preference');
      } finally {
          setLoading(false);
      }
  };

  const handleDeclineAgreement = async () => {
      alert("You will not be allowed to use OpenGadget services without accepting the Agreement.");
      setShowAgreement(false);
      try {
          await supabase.auth.signOut();
      } catch (e) {
          console.error(e);
      }
      onClose();
  };

  const handleAuth = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const cleanEmail = email.replace(/[\s\u200B-\u200D\uFEFF]/g, '');

      if (isLoginView) {
        // Login
        const { data, error: signInError } = await signIn(cleanEmail, password);
        
        if (signInError) {
          if (signInError.message?.includes('Email not confirmed')) {
            throw new Error('Your email address needs to be confirmed. Please check your inbox for the confirmation email.');
          }
          throw signInError;
        }
        
        if (data?.user) {
           await userService.recordActivity(data.user.id, navigator.userAgent, "Login", window.location.pathname).catch(e => console.error('Activity log error:', e));
           
           // Check for required agreement
           const { data: agreementData } = await supabase
            .from('seller_agreements_versions')
            .select('id, content')
            .eq('is_active', true)
            .eq('agreement_type', 'buyer')
            .maybeSingle();

           if (agreementData) {
              // Check if user already signed this version
              const { data: existing } = await supabase
                .from('seller_agreements')
                .select('id')
                .eq('user_id', data.user.id)
                .eq('version_id', agreementData.id)
                .maybeSingle();

              if (!existing) {
                setAgreementText(agreementData.content);
                setAgreementVersionId(agreementData.id);
                setPendingUserId(data.user.id);
                setShowAgreement(true);
                setLoading(false);
                return; // Stop flow to show agreement
              }
           }
        }
        
        setSuccessMessage('Logged in successfully!');
        onSuccess(); 
        onClose();
      } else {
        // Sign up
        if (password !== confirmPassword) {
          throw new Error('Passwords do not match.');
        }

        // NEW: Check if email already exists using RPC function
        try {
          const { data: exists, error: checkError } = await supabase
            .rpc('check_email_exists', { email_to_check: email });

          if (checkError) {
            console.error('Error checking existing email:', checkError);
          } else if (exists) {
            setError('An account already exists with that email. Please choose a different email or request a password reset.');
            setLoading(false);
            return;
          }
        } catch (checkErr) {
          console.warn('Could not pre-verify email availability:', checkErr.message);
        }

        const { data: signUpData, error: signUpError } = await supabase.auth.signUp({ 
          email, 
          password,
          options: {
            data: {
              first_name: firstName,
              last_name: lastName
            }
          }
        });
        
        if (signUpError) throw signUpError;
        
        if (signUpData?.user) {
           await userService.recordActivity(signUpData.user.id, navigator.userAgent, "Signup", "Account Created via Modal").catch(e => console.error('Activity log error:', e));
        }

        setSuccessMessage('Signed up successfully! Please check your email for a confirmation link.');
      }
    } catch (err) {
      setError(err.message);
      console.error('Auth error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black bg-opacity-75 backdrop-blur-sm px-4" onClick={onClose}>
      <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full relative max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
          <Icon name="X" size={20} />
        </button>

        <div className="text-center mb-6">
            <img src="/assets/images/OpenGadget-SMALL-GREY-TRANS-1763756659192.png" alt="OpenGadget Logo" className="mx-auto h-12 w-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900">
              {isLoginView ? 'Welcome Back' : 'Join OpenGadget'}
            </h2>
        </div>

        {error && (
          <div className="bg-red-50 text-red-700 border border-red-200 rounded-md p-3 mb-4 text-sm">
            {error}
          </div>
        )}
        {successMessage && (
          <div className="bg-green-50 text-green-700 border border-green-200 rounded-md p-3 mb-4 text-sm text-center">
            {successMessage}
          </div>
        )}

        {!successMessage && (
          <form onSubmit={handleAuth} className="space-y-4 text-left">
            {!isLoginView && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-bold text-gray-700">
                    First Name
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required={!isLoginView}
                    placeholder="First"
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-bold text-gray-700">
                    Last Name
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    className="mt-1 block w-full rounded-md border border-gray-300 py-2 px-3 text-gray-900 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required={!isLoginView}
                    placeholder="Last"
                  />
                </div>
              </div>
            )}

            <Input
              label="Email"
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500"
            />

            <Input
              label="Password"
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete={isLoginView ? 'current-password' : 'new-password'}
              className="bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500"
            />

            {!isLoginView && (
              <Input
                label="Confirm Password"
                type="password"
                id="confirm-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required={!isLoginView}
                autoComplete="new-password"
                className="bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500"
              />
            )}

            <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4" disabled={loading}>
              {loading ? (
                <Icon name="Loader2" className="animate-spin mr-2" size={20} />
              ) : isLoginView ? (
                'Log In'
              ) : (
                'Sign Up'
              )}
            </Button>
          </form>
        )}

        <div className="mt-6 text-center">
          <button onClick={() => setIsLoginView(!isLoginView)} className="text-sm text-blue-600 font-bold hover:underline">
            {isLoginView ? "Don't have an account? Sign Up" : 'Already have an account? Log In'}
          </button>
          {!isLoginView && <p className="text-xs text-gray-500 mt-2">Account sign up is free</p>}
        </div>
      </div>
    </div>

    {/* Agreement Modal - Same logic as Signin Page */}
    <Modal isOpen={showAgreement} onClose={handleDeclineAgreement} title="Updated Service Terms" size="max-w-4xl">
         <div className="p-8 text-left">
            <div className="mb-6 p-4 bg-blue-50 border border-blue-100 rounded-lg flex items-start gap-3">
               <Icon name="Info" className="text-blue-600 mt-0.5" size={20} />
               <p className="text-sm text-blue-800 font-medium">
                 We have updated our terms, please read and accept to continue use of the OpenGadget services.
               </p>
            </div>
            <div className="prose max-w-none mb-8 h-96 overflow-y-auto bg-white p-6 border rounded-xl shadow-inner text-slate-700" dangerouslySetInnerHTML={{ __html: agreementText }} />
            <div className="flex justify-end gap-4 pt-4 border-t">
                <button 
                  onClick={handleDeclineAgreement}
                  className="px-6 py-2.5 bg-white border border-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-50 transition-all"
                >
                  Decline & Logout
                </button>
                <button 
                  onClick={handleAcceptAgreement}
                  className="px-8 py-2.5 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 shadow-lg shadow-blue-600/20 transition-all hover:scale-105 active:scale-95"
                >
                  I Accept
                </button>
            </div>
         </div>
      </Modal>
    </>
  );
};

export default LoginSignupModal;
